import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSharedState } from "sharedStateProvider";
import PageLayout from "components/page-layout";
import { getMonitoringDashboard } from "api/location-monitoring";
import { MonitoringDashboardResponse } from "types/location-monitoring-type";
import { IconMapPin, IconUsers, IconClock, IconAlertTriangle, IconCheck, IconX } from "@tabler/icons-react";

// Helper function to safely parse dates
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  } catch {
    return "-";
  }
};

export default function LocationMonitoring() {
  const navigate = useNavigate();
  const { notify } = useSharedState();
  const [dashboardData, setDashboardData] = useState<MonitoringDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data: MonitoringDashboardResponse = await getMonitoringDashboard();
      setDashboardData(data);
    } catch (error: any) {
      const err = error?.response?.data?.error || "Ошибка при загрузке данных мониторинга";
      notify(err, "danger", "Ошибка");
    } finally {
      setIsLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const dashboardContent = dashboardData ? (
    <div className="row row-cards">
      {/* Main Statistics Cards */}
      <div className="col-sm-6 col-lg-3">
        <div 
          className="card" 
          onClick={() => navigate("/location-users?inside_factory=true")}
          role="button"
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/location-users?inside_factory=true");
            }
          }}
          style={{ 
            cursor: "pointer",
            transition: "transform 0.2s ease-in-out"
          }}
          onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = "scale(1.02)";
            e.currentTarget.style.boxShadow = "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075), 0 0.5rem 1rem rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "";
          }}
        >
          <div className="card-body">
            <div className="d-flex align-items-center">
              <div className="subheader">Сотрудники внутри</div>
            </div>
            <div className="h1 mb-3">{dashboardData.employees_inside || 0}</div>
            <div className="d-flex align-items-center">
              <IconUsers className="icon me-1 text-muted" />
              <span className="text-muted">в здании</span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <div className="subheader">Отслеживание активно</div>
            </div>
            <div className="h1 mb-3">{dashboardData.location_tracking_active || 0}</div>
            <div className="d-flex align-items-center">
              <IconMapPin className="icon me-1 text-muted" />
              <span className="text-muted">активных</span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <div className="subheader">Ожидающие запросы</div>
            </div>
            <div className="h1 mb-3">{dashboardData.pending_tracking_requests?.count || 0}</div>
            <div className="d-flex align-items-center">
              <IconClock className="icon me-1 text-muted" />
              <span className="text-muted">
                {dashboardData.pending_tracking_requests?.average_wait_minutes 
                  ? `ср. ${dashboardData.pending_tracking_requests.average_wait_minutes.toFixed(1)} мин`
                  : ""}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <div className="subheader">Успешность отслеживания</div>
            </div>
            <div className="h1 mb-3">
              {dashboardData.tracking_efficiency?.success_rate 
                ? `${dashboardData.tracking_efficiency.success_rate.toFixed(1)}%`
                : "0%"}
            </div>
            <div className="d-flex align-items-center">
              <span className={`badge ${
                (dashboardData.tracking_efficiency?.success_rate || 0) >= 90 
                  ? "bg-success" 
                  : (dashboardData.tracking_efficiency?.success_rate || 0) >= 70 
                    ? "bg-warning" 
                    : "bg-danger"
              }`}>
                {(dashboardData.tracking_efficiency?.success_rate || 0) >= 90 
                  ? "Отлично" 
                  : (dashboardData.tracking_efficiency?.success_rate || 0) >= 70 
                    ? "Хорошо" 
                    : "Требует внимания"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Tracking Requests Details */}
      {dashboardData.pending_tracking_requests && dashboardData.pending_tracking_requests.count > 0 && (
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <IconAlertTriangle className="icon me-1" />
                Детали ожидающих запросов
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="text-muted">Всего ожидающих</div>
                  <div className="h2">{dashboardData.pending_tracking_requests.count}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted">Среднее время ожидания</div>
                  <div className="h3">
                    {dashboardData.pending_tracking_requests.average_wait_minutes.toFixed(1)} мин
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted">Минимальное время</div>
                  <div className="h3">
                    {dashboardData.pending_tracking_requests.min_wait_minutes.toFixed(1)} мин
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted">Максимальное время</div>
                  <div className="h3">
                    <span className={dashboardData.pending_tracking_requests.max_wait_minutes > 5 ? "text-danger" : ""}>
                      {dashboardData.pending_tracking_requests.max_wait_minutes.toFixed(1)} мин
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Efficiency Details */}
      {dashboardData.tracking_efficiency && (
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Эффективность отслеживания</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="text-muted">Процент успешного запуска отслеживания</div>
                  <div className="h2">
                    {dashboardData.tracking_efficiency.success_rate.toFixed(1)}%
                  </div>
                  <div className="progress progress-sm mt-2">
                    <div
                      className="progress-bar"
                      style={{ width: `${dashboardData.tracking_efficiency.success_rate}%` }}
                      role="progressbar"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="text-muted">Среднее время до запуска отслеживания</div>
                  <div className="h2">
                    {dashboardData.tracking_efficiency.average_start_time_minutes.toFixed(1)} мин
                  </div>
                  <div className="text-muted mt-1">
                    после события ENTER
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SKUD Sync Status */}
      {dashboardData.skud_sync_status && (
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                Состояние синхронизации SKUD
                {dashboardData.skud_sync_status.is_healthy ? (
                  <span className="badge bg-success ms-2">
                    <IconCheck className="icon" /> Здорово
                  </span>
                ) : (
                  <span className="badge bg-danger ms-2">
                    <IconX className="icon" /> Проблема
                  </span>
                )}
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="text-muted">Последняя синхронизация</div>
                  <div>{formatDate(dashboardData.skud_sync_status.last_sync_time)}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted">Время с последней синхронизации</div>
                  <div className={`h3 ${dashboardData.skud_sync_status.is_healthy ? "" : "text-danger"}`}>
                    {dashboardData.skud_sync_status.time_since_sync.toFixed(1)} мин
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted">Статус</div>
                  <div>
                    {dashboardData.skud_sync_status.is_healthy ? (
                      <span className="badge bg-success">
                        <IconCheck className="icon" /> Синхронизация работает нормально
                      </span>
                    ) : (
                      <span className="badge bg-danger">
                        <IconX className="icon" /> Синхронизация задерживается
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Quality */}
      {dashboardData.data_quality && (
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Качество данных</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-12">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="text-muted">События без привязки к пользователю</div>
                      <div className="h3">
                        {dashboardData.data_quality.events_with_missing_user_mappings || 0}
                      </div>
                      <div className="text-muted">за последние 24 часа</div>
                    </div>
                    {dashboardData.data_quality.events_with_missing_user_mappings > 0 && (
                      <div>
                        <span className="badge bg-warning">
                          <IconAlertTriangle className="icon" /> Требует внимания
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body text-center">
            <p className="text-muted">Нет данных для отображения</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageLayout
        children={dashboardContent}
        isLoading={isLoading}
        filters={false}
        search={false}
        header="Позиционирование - Мониторинг"
        subheaderText=""
        dataLength={dashboardData ? 1 : 0}
        dateRange={false}
      />
    </>
  );
}
